// React
import React, { useMemo } from 'react';

import Image from 'next/legacy/image';
import Link from 'next/link';
import cc from 'classcat';
import t from 'prop-types';
import dayjs from 'dayjs';

// Utilities
import { useFormat, useLabels, useUser, usePermissions } from 'utilities/hooks';
import { useInitiativeDataStore } from 'utilities/store';

// Components
import Pill from 'components/pill';
import ReportType from 'components/reportType';

// Icons
import { FiActivity, FiExternalLink, FiLock, FiStar } from 'react-icons/fi';

const InitiativeRowComponent = ({
    item,
    reports,
    fundingRecipients,
    funders,
    limitedAccess,
    link,
}) => {
    // ///////////////////
    // STORES
    // ///////////////////

    // const limitedAccess = true;

    const { CONSTANTS } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label, getValueLabel } = useLabels();
    const format = useFormat();
    const { getUserAccountType, getUserAccountId } = useUser();
    const { ACCOUNTS } = usePermissions();

    // ///////////////////
    // DATA
    // ///////////////////

    const ownInitiative =
        getUserAccountType() === ACCOUNTS.funder &&
        funders.map(f => f.Id).includes(getUserAccountId());

    const {
        Name,
        Category__c,
        Grant_Start_Date__c,
        Grant_End_Date__c,
        Hero_Image_URL__c,
    } = item;

    const sortedReports = useMemo(
        () =>
            reports
                .sort(
                    (a, b) => new Date(a.Due_Date__c) - new Date(b.Due_Date__c)
                )
                .filter(report => report.Status__c !== 'Published')
                .filter(report => report.Report_Type__c !== 'Milestone')
                .filter(report => report.Funding_Report__c)
                .map(report => {
                    const dueDate = format.date(report.Due_Date__c);
                    const overdueDays = dayjs(dayjs()).diff(
                        report.Due_Date__c,
                        'day'
                    );
                    return {
                        ...report,
                        dueDate,
                        overdueDays,
                    };
                })
                .filter(report => report.overdueDays < 120),

        [reports]
    );

    const nextReport = sortedReports?.[0] ?? null;

    const pillThemes = {
        [CONSTANTS.REPORTS.REPORT_NOT_STARTED]: 'coral',
        [CONSTANTS.REPORTS.REPORT_IN_PROGRESS]: 'amber',
        [CONSTANTS.REPORTS.REPORT_IN_REVIEW]: 'amber',
        [CONSTANTS.REPORTS.REPORT_PUBLISHED]: 'teal',
    };

    // ///////////////////
    // RENDER
    // ///////////////////

    function wrapInitiativeLink(children, link) {
        return !limitedAccess ? <Link href={link}>{children}</Link> : children;
    }

    function wrapReportWithLink(children, link) {
        return !limitedAccess ? (
            <Link className="w-full" href={link}>
                {children}
            </Link>
        ) : (
            children
        );
    }

    return (
        <div
            className={cc([
                'flex flex-col justify-between p-16 bg-white md:flex-row rounded-8',
            ])}>
            {/* Initiative */}
            {wrapInitiativeLink(
                <div
                    className={cc([
                        'flex',
                        {
                            'cursor-pointer': !limitedAccess,
                        },
                    ])}>
                    {/* Image/Icon */}
                    <div className="flex-col items-center justify-center shrink-0 hidden space-y-12 w-128 min-h-128  rounded-8 sm:flex min-h-[128px] p-8 text-center relative">
                        {Hero_Image_URL__c ? (
                            <Image
                                src={Hero_Image_URL__c}
                                layout="fill"
                                objectFit="cover"
                                className="rounded-8"
                            />
                        ) : (
                            <FiActivity className="w-48 h-48" />
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex flex-col sm:ml-16 mr-80">
                        {(limitedAccess || ownInitiative) && (
                            <div className="flex gap-3 mb-3">
                                {ownInitiative && (
                                    <Pill
                                        theme="amber"
                                        size="small"
                                        className="flex items-center gap-2 !font-bold">
                                        <FiStar className="w-12 h-12 -mt-3 fill-current" />
                                        {label(
                                            'InitiativeManagerCardYourInitiative'
                                        )}
                                    </Pill>
                                )}
                                {limitedAccess && (
                                    <Pill
                                        theme="coral"
                                        size="small"
                                        className="flex items-center gap-2 !font-bold">
                                        <FiLock className="w-12 h-12 -mt-3" />
                                        {label(
                                            'InitiativeManagerCardLimitedAccess'
                                        )}
                                    </Pill>
                                )}
                            </div>
                        )}

                        <Pill theme="blue" className="inline-block mb-20">
                            {getValueLabel(
                                'Initiative__c.Category__c',
                                Category__c
                            )}
                        </Pill>

                        <div className="t-caption text-blue-60">
                            {format.fundingRecipientNamesFull(
                                fundingRecipients
                            )}
                        </div>
                        <div className="text-blue-100 t-h5">{Name}</div>
                        <div className="text-blue-60 t-footnote">
                            {label('InitiativeRowSupportedBy')}{' '}
                            {format.funderNames(funders)}
                        </div>
                        {Grant_Start_Date__c && Grant_End_Date__c && (
                            <div className="mt-8 text-blue-100 t-caption-bold">
                                {`${format.date(
                                    Grant_Start_Date__c
                                )} - ${format.date(Grant_End_Date__c)}`}
                            </div>
                        )}
                    </div>
                </div>,
                link
            )}

            {/* Dates */}
            <div className="flex self-end mt-24 space-x-12 flex-shrink-0 w-full md:w-[30%] min-w-[260px] md:space-y-12 md:flex-col md:space-x-0 md:mt-0">
                {nextReport &&
                    wrapReportWithLink(
                        <div
                            className={cc([
                                'flex flex-col bg-white',
                                {
                                    'group ': !limitedAccess,
                                    'cursor-default': limitedAccess,
                                },
                            ])}>
                            <div
                                className={cc([
                                    'p-8 space-y-4 border-4 md:w-auto border-amber-20 transition-colors group-hover:border-amber-40 pr-0',
                                    {
                                        'rounded-t-4': !limitedAccess,
                                        'rounded-4': limitedAccess,
                                    },
                                ])}>
                                <div className="flex items-center space-x-8">
                                    <ReportType
                                        {...{
                                            type: nextReport.Report_Type__c,
                                            size: 'small',
                                        }}
                                    />
                                    <div className="flex flex-col space-y-2">
                                        <div className="flex items-baseline space-x-8 whitespace-nowrap">
                                            <span className="text-blue-100 t-sh-7">
                                                {label(
                                                    'InitiativeManagerCardDeadline'
                                                )}{' '}
                                            </span>
                                            <span className="t-footnote text-blue-60">
                                                {nextReport.dueDate}
                                            </span>
                                        </div>
                                        <div className="flex space-x-8 whitespace-nowrap">
                                            <span className="text-blue-100 t-caption-bold">
                                                {getValueLabel(
                                                    'Initiative_Report__c.Report_Type__c',
                                                    nextReport.Report_Type__c
                                                )}
                                            </span>
                                            <span className="t-footnote text-blue-60">
                                                <Pill
                                                    theme={
                                                        pillThemes[
                                                            nextReport
                                                                ?.Status__c
                                                        ]
                                                    }>
                                                    {getValueLabel(
                                                        'Initiative_Report__c.Status__c',
                                                        nextReport?.Status__c
                                                    )}
                                                </Pill>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {!limitedAccess && (
                                <div className="flex items-center self-stretch justify-center flex-shrink-0 w-full p-6 transition-colors rounded-b-4 bg-amber-20 text-amber-120 group-hover:bg-amber-40">
                                    <FiExternalLink className="w-16 h-16 mb-4 -mt-2" />
                                </div>
                            )}
                        </div>,
                        `/${item.Id}/reports/${nextReport.Id}`
                    )}
            </div>
        </div>
    );
};

InitiativeRowComponent.propTypes = {
    item: t.object.isRequired,
    reports: t.array,
    fundingRecipients: t.array,
    funders: t.array,
};

InitiativeRowComponent.defaultProps = {
    item: {},
    reports: [],
    fundingRecipients: [],
    funders: [],
};

export default InitiativeRowComponent;
